/**
 * Zero-knowledge crypto module
 *
 * Vault key architecture:
 *   A random 256-bit vaultKey encrypts all vault items.
 *   After OPAQUE login/registration, the exportKey is used via HKDF to derive
 *   a wrapping key that encrypts the safeKey for server storage.
 *   A local PBKDF2 wrapping key encrypts the safeKey in IndexedDB for offline unlock.
 *
 * Vault item encryption:
 *   plaintext JSON → AES-256-GCM (random 12-byte IV) → ciphertext + IV stored together
 */

const PBKDF2_ITERATIONS = 600_000;
const IV_LENGTH = 12; // bytes, recommended for AES-GCM

// ─── Vault Key Generation ─────────────────────────────────────────────────────

export async function generateVaultKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

export function generateLocalKdfSalt(): string {
  const salt = crypto.getRandomValues(new Uint8Array(32) as Uint8Array<ArrayBuffer>);
  return bytesToBase64(salt);
}

// ─── Wrapping Key Derivation ──────────────────────────────────────────────────

export async function deriveWrappingKeyFromExportKey(exportKey: string): Promise<CryptoKey> {
  const normalized = exportKey.replace(/-/g, '+').replace(/_/g, '/');
  const keyBytes = base64ToBytes(normalized);

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    'HKDF',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32),
      info: new TextEncoder().encode('vault-key-wrapping'),
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function deriveLocalWrappingKey(
  masterPassword: string,
  email: string,
  saltBase64: string
): Promise<CryptoKey> {
  const enc = new TextEncoder();

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(masterPassword),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  const saltBytes = base64ToBytes(saltBase64);
  const emailBytes = enc.encode(email.toLowerCase().trim());
  const combinedSalt = new Uint8Array(saltBytes.length + emailBytes.length);
  combinedSalt.set(saltBytes);
  combinedSalt.set(emailBytes, saltBytes.length);

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: combinedSalt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// ─── Vault Key Wrap / Unwrap ──────────────────────────────────────────────────

export async function wrapVaultKey(
  vaultKey: CryptoKey,
  wrappingKey: CryptoKey
): Promise<{ wrapped: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH) as Uint8Array<ArrayBuffer>);
  const rawKeyBytes = await crypto.subtle.exportKey('raw', vaultKey);

  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    wrappingKey,
    rawKeyBytes
  );

  return {
    wrapped: bytesToBase64(new Uint8Array(ciphertextBuffer)),
    iv: bytesToBase64(iv),
  };
}

export async function unwrapVaultKey(
  wrappedBase64: string,
  ivBase64: string,
  wrappingKey: CryptoKey
): Promise<CryptoKey> {
  const iv = base64ToBytes(ivBase64);
  const ciphertext = base64ToBytes(wrappedBase64);

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    wrappingKey,
    ciphertext
  );

  return crypto.subtle.importKey(
    'raw',
    decryptedBuffer,
    { name: 'AES-GCM' },
    true,
    ['encrypt', 'decrypt']
  );
}

// ─── Vault Item Encryption / Decryption ──────────────────────────────────────

export async function encryptItem(
  item: object,
  encryptionKey: CryptoKey
): Promise<{ ciphertext: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH) as Uint8Array<ArrayBuffer>);
  const plaintext = new TextEncoder().encode(JSON.stringify(item));

  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    encryptionKey,
    plaintext
  );

  return {
    ciphertext: bytesToBase64(new Uint8Array(ciphertextBuffer)),
    iv: bytesToBase64(iv),
  };
}

export async function decryptItem<T = unknown>(
  ciphertextBase64: string,
  ivBase64: string,
  encryptionKey: CryptoKey
): Promise<T> {
  const iv = base64ToBytes(ivBase64);
  const ciphertext = base64ToBytes(ciphertextBase64);

  const plaintextBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    encryptionKey,
    ciphertext
  );

  const plaintext = new TextDecoder().decode(plaintextBuffer);
  return JSON.parse(plaintext) as T;
}

// ─── Password Generator ───────────────────────────────────────────────────────

export interface PasswordOptions {
  length: number;
  uppercase: boolean;
  lowercase: boolean;
  numbers: boolean;
  symbols: boolean;
  excludeAmbiguous: boolean;
  /**
   * Restrict the symbol pool to exactly these characters (used with `symbols:
   * true`). Set from a site's password rules so a generated password only ever
   * contains symbols the site permits. Empty/absent ⇒ the default symbol set.
   */
  symbolSet?: string;
}

const CHARSETS = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  uppercaseUnambiguous: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  lowercaseUnambiguous: 'abcdefghjkmnpqrstuvwxyz',
  numbers: '0123456789',
  numbersUnambiguous: '23456789',
  symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
  // `|` is visually confusable with `l` / `I` / `1`.
  symbolsUnambiguous: '!@#$%^&*()_+-=[]{};:,.<>?',
};

/**
 * Target distribution of character classes in generated passwords —
 * 70% letters / 20% numbers / 10% symbols. Drawing classes by weight
 * (instead of pooling everything into one uniform charset) makes
 * outputs feel more password-like to humans without meaningfully
 * sacrificing entropy: a typical 18-char password loses ~1 bit total
 * compared to uniform sampling over the same character pool.
 *
 * A class with no characters enabled (e.g. symbols off) is excluded
 * from the draw; the remaining classes keep their relative weights.
 */
export const PASSWORD_CLASS_WEIGHTS = {
  letters: 70,
  numbers: 20,
  symbols: 10,
} as const;

export function generatePassword(options: PasswordOptions): string {
  // Build per-class character pools, applying the ambiguous-character
  // filter. "Letters" pools uppercase + lowercase together (mixed-case)
  // because the 70% weight is on the *letters* class as a whole, not
  // on each case individually.
  let letters = '';
  if (options.uppercase) {
    letters += options.excludeAmbiguous ? CHARSETS.uppercaseUnambiguous : CHARSETS.uppercase;
  }
  if (options.lowercase) {
    letters += options.excludeAmbiguous ? CHARSETS.lowercaseUnambiguous : CHARSETS.lowercase;
  }
  const numbers = options.numbers
    ? (options.excludeAmbiguous ? CHARSETS.numbersUnambiguous : CHARSETS.numbers)
    : '';
  const symbols = options.symbols
    ? (options.symbolSet && options.symbolSet.length
        ? options.symbolSet
        : (options.excludeAmbiguous ? CHARSETS.symbolsUnambiguous : CHARSETS.symbols))
    : '';

  const buckets: Array<[string, number]> = [];
  if (letters) buckets.push([letters, PASSWORD_CLASS_WEIGHTS.letters]);
  if (numbers) buckets.push([numbers, PASSWORD_CLASS_WEIGHTS.numbers]);
  if (symbols) buckets.push([symbols, PASSWORD_CLASS_WEIGHTS.symbols]);
  if (buckets.length === 0) {
    // No category enabled — fall back to lowercase + numbers at their
    // canonical weights so the generator never returns empty.
    buckets.push([CHARSETS.lowercase, PASSWORD_CLASS_WEIGHTS.letters]);
    buckets.push([CHARSETS.numbers, PASSWORD_CLASS_WEIGHTS.numbers]);
  }

  const totalWeight = buckets.reduce((sum, [, w]) => sum + w, 0);
  const maxUsableBucket = 256 - (256 % totalWeight);

  // Generate plenty of bytes upfront — each output char consumes 1–2
  // bytes (one for the class roll, one for the within-class roll), and
  // rejection sampling discards a small fraction. Refill if we run out.
  let password = '';
  while (password.length < options.length) {
    const bytes = crypto.getRandomValues(new Uint8Array(options.length * 4));
    for (let i = 0; i + 1 < bytes.length && password.length < options.length; ) {
      const bucketRoll = bytes[i++];
      if (bucketRoll >= maxUsableBucket) continue;

      // Walk cumulative weights to find which bucket the roll lands in.
      let chosen = '';
      let cumulative = 0;
      const idx = bucketRoll % totalWeight;
      for (const [chars, weight] of buckets) {
        cumulative += weight;
        if (idx < cumulative) {
          chosen = chars;
          break;
        }
      }

      const charRoll = bytes[i++];
      const maxUsableChar = 256 - (256 % chosen.length);
      if (charRoll >= maxUsableChar) continue;
      password += chosen[charRoll % chosen.length];
    }
  }

  return password;
}

// ─── Utility Functions ────────────────────────────────────────────────────────

export function bytesToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)) as Uint8Array<ArrayBuffer>;
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}
